import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Clock, Zap, BarChart3 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageLayout } from '@/components/layout/PageLayout';
import {
  EmbeddedSkillRail,
  WorkspacePatternsPanel,
  parseWorkspacePatternsFromSkillData,
} from '@/components/skill';
import type { WorkspacePattern } from '@/components/skill';
import type { SkillExecutionResult } from '@/services/skills';
import {
  tabService,
  mapSkillPredictionsToUi,
  formatPredictionTimeSaved,
  estimatePotentialTimeSavedMinutes,
  type TabPrediction,
  type PredictionStats,
} from '../services/tabs';
import { sessionIntelligenceService } from '../services/sessionIntelligence';
import { toast } from 'sonner';

function friendlyTabPredictionError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (
    /parse tab predictions json|invalid character.*after top-level/i.test(raw)
  ) {
    return 'The prediction model returned an unreadable response. Try again, or browse with more tabs open for stronger signals.';
  }
  if (
    /status 401|invalid_request_error|API returned status|completion failed|all retry attempts failed/i.test(
      raw
    ) &&
    raw.length > 120
  ) {
    return 'Predictions need a working LLM on the server. Set OPENAI_API_KEY (or your configured provider key), restart the backend, and retry.';
  }
  return raw.length > 400 ? `${raw.slice(0, 400)}…` : raw;
}

export function TabPredictionEngine() {
  const [predictions, setPredictions] = useState<TabPrediction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [noPredictionsStatus, setNoPredictionsStatus] = useState<string | null>(null);
  const [workspaceSessionId, setWorkspaceSessionId] = useState<string | null>(null);
  const [workspacePatterns, setWorkspacePatterns] = useState<WorkspacePattern[]>([]);
  const [predictionStats, setPredictionStats] = useState<PredictionStats | null>(null);
  const patternsPanelRef = useRef<HTMLDivElement>(null);

  const loadPredictionStats = useCallback(async () => {
    try {
      const stats = await tabService.getPredictionStats();
      setPredictionStats(stats);
    } catch {
      setPredictionStats(null);
    }
  }, []);

  const handleSkillExecuted = useCallback(
    (skill: { id: string }, result: SkillExecutionResult) => {
      if (result.status !== 'success') return;

      if (skill.id === 'analyze_workspace') {
        const parsed = parseWorkspacePatternsFromSkillData(result.data);
        setWorkspacePatterns(parsed);
        if (parsed.length > 0) {
          requestAnimationFrame(() => {
            patternsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
        return;
      }

      if (skill.id === 'predict_tabs' || skill.id === 'cross_device_sync_predict') {
        const preds = mapSkillPredictionsToUi(result.data);
        setLoadError(null);
        if (preds.length > 0) {
          setPredictions(preds);
          setNoPredictionsStatus(null);
        } else {
          setPredictions([]);
          setNoPredictionsStatus('no_predictions');
        }
        void loadPredictionStats();
      }
    },
    [loadPredictionStats],
  );

  const fetchPredictions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    setNoPredictionsStatus(null);
    try {
      const sessionIds = await sessionIntelligenceService.listWorkspaceSessionIds();
      setWorkspaceSessionId(sessionIds[0] ?? null);
      let context = 'no open tabs';
      if (sessionIds.length > 0) {
        const { tabs } = await tabService.getTabsBySession(sessionIds[0], 1, 50);
        if (tabs.length > 0) {
          context = tabs.map(t => `${t.title ?? ''} | ${t.url ?? ''}`).join('\n');
        }
      }
      const result = await tabService.getPredictions(context);
      setPredictions(result.predictions);
      setNoPredictionsStatus(result.status ?? null);
      await loadPredictionStats();
    } catch (error) {
      console.error('Failed to fetch predictions:', error);
      const msg = friendlyTabPredictionError(error) || 'Failed to load predictions';
      setLoadError(msg);
      setPredictions([]);
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  }, [loadPredictionStats]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const handleApplyPrediction = async (pred: TabPrediction) => {
    const u = pred.url?.trim();
    if (!u) {
      toast.error('No URL for this prediction');
      return;
    }
    window.open(u, '_blank', 'noopener,noreferrer');
    try {
      await tabService.applyPrediction(pred.id, true);
      await loadPredictionStats();
    } catch {
      /* feedback is best-effort */
    }
    toast.success('Opened in new tab');
  };

  const avgConfidence =
    predictions.length > 0
      ? Math.round(predictions.reduce((sum, p) => sum + p.confidence, 0) / predictions.length)
      : null;

  const timeSavedMinutes =
    predictionStats && predictionStats.feedback_count > 0
      ? predictionStats.time_saved_minutes
      : estimatePotentialTimeSavedMinutes(predictions);

  const accuracyPercent =
    predictionStats && predictionStats.feedback_count > 0
      ? Math.round(predictionStats.accuracy_percent)
      : avgConfidence;

  return (
    <PageLayout
      title="Tab Prediction Engine"
      description="AI predicts which tabs you'll need next based on your patterns"
    >
      <div ref={patternsPanelRef}>
        <EmbeddedSkillRail
          cardClassName="mb-6"
          title="Prediction Skills"
          description="Skills that read, predict, or preload tabs in this view."
          skillIds={['predict_tabs', 'smart_preload', 'analyze_workspace', 'cross_device_sync_predict', 'auto_organize']}
          sessionId={workspaceSessionId ?? undefined}
          preferExecute
          onSkillExecuted={handleSkillExecuted}
        >
          <WorkspacePatternsPanel
            patterns={workspacePatterns}
            onDismiss={() => setWorkspacePatterns([])}
          />
        </EmbeddedSkillRail>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        {[
          {
            label: 'Predictions',
            value: String(predictions.length),
            icon: <Sparkles className="w-8 h-8 text-primary-400" />,
          },
          {
            label: 'Avg Confidence',
            value: avgConfidence !== null ? `${avgConfidence}%` : '—',
            icon: <BarChart3 className="w-8 h-8 text-info-400" />,
          },
          {
            label: 'Time saved',
            value: formatPredictionTimeSaved(timeSavedMinutes),
            icon: <Clock className="w-8 h-8 text-success-400" />,
          },
          {
            label: 'Accuracy',
            value: accuracyPercent !== null ? `${accuracyPercent}%` : '0%',
            icon: <Zap className="w-8 h-8 text-warning-400" />,
          },
        ].map((stat, idx) => (
          <Card key={idx} className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-khiimori-text-muted">{stat.label}</p>
                <p className="text-3xl font-bold text-khiimori-text-primary">{stat.value}</p>
              </div>
              {stat.icon}
            </div>
          </Card>
        ))}
      </div>

      {loadError && (
        <Card className="p-4 mt-6 border-error-500/30 bg-error-500/5">
          <p className="text-sm text-khiimori-text-primary mb-2">{loadError}</p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchPredictions()}
          >
            Retry
          </Button>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      ) : predictions.length === 0 && !loadError ? (
        <Card className="p-8 mt-6 text-center text-khiimori-text-muted">
          {noPredictionsStatus === 'no_predictions' ? (
            <>
              <p className="mb-2">No predictions generated. The LLM received your open tabs but found no strong signals to predict next tabs.</p>
              <p className="text-sm">Try browsing with more tabs open to give the model more patterns to work with.</p>
            </>
          ) : (
            <>
              No predictions yet. Ensure the LLM is configured and try again, or use the app until patterns are
              available.
            </>
          )}
        </Card>
      ) : predictions.length > 0 ? (
        <div className="space-y-4 mt-6">
          <h3 className="text-lg font-semibold text-khiimori-text-primary">Upcoming Predictions</h3>
          {predictions.map((pred, idx) => (
            <motion.div
              key={pred.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: idx * 0.1 }}
            >
              <Card className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h4 className="text-lg font-semibold text-khiimori-text-primary">{pred.tab}</h4>
                      <span className="px-3 py-1 bg-primary-500/20 text-primary-400 rounded-full text-xs font-medium">
                        {pred.category}
                      </span>
                    </div>
                    <p className="text-sm text-khiimori-text-muted mb-2 break-all">{pred.url}</p>
                    <p className="text-sm text-khiimori-text-primary mb-3">
                      <Sparkles className="w-4 h-4 inline mr-1" />
                      {pred.reason}
                    </p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-khiimori-text-muted">Confidence:</span>
                        <div className="w-32 bg-khiimori-bg-elevated/10 rounded-full h-2">
                          <div
                            className="bg-primary-500 h-2 rounded-full"
                            style={{ width: `${Math.min(100, pred.confidence)}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-primary-400">
                          {pred.confidence}%
                        </span>
                      </div>
                      <span className="text-sm text-khiimori-text-muted">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {pred.timing}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    onClick={() => handleApplyPrediction(pred)}
                    className="shrink-0"
                  >
                    Open Now
                  </Button>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : null}
    </PageLayout>
  );
}
